import { createContext, useEffect, useReducer } from "react";
import { AppContextType, AppState, MessageData } from "./core/types";
import { resolveSticker, Sticker } from "./core/stickers";
import ChatBar from "./components/chat_bar";
import { ChatWindow } from "./components/chat_window";

import "./App.css";

export const AppContext = createContext<AppContextType>({
  messages: [],
  unlockedStickers: new Set(),
  showStickersMenu: false,
  aliIsTyping: false,
  dispatch: () => {},
});

type Action =
  | { type: "SEND_MESSAGE"; payload: MessageData }
  | { type: "UNLOCK_STICKER"; payload: Sticker }
  | { type: "TOGGLE_STICKERS_MENU" }
  | { type: "SET_ALI_TYPING"; payload: boolean };

const STORAGE_KEY = "whatsapp_state";

const DEFAULT_STATE: AppState = {
  messages: [],
  unlockedStickers: new Set<Sticker>(),
  showStickersMenu: false,
  aliIsTyping: false,
};

function loadState(): AppState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_STATE,
      ...parsed,
      unlockedStickers: new Set<Sticker>(parsed.unlockedStickers ?? []),
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function saveState(state: AppState) {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      messages: state.messages,
      unlockedStickers: Array.from(state.unlockedStickers),
    }),
  );
}

function reducer(state: AppState, action: Action) {
  switch (action.type) {
    case "SEND_MESSAGE": {
      const unlocked = new Set(state.unlockedStickers);
      const input = action.payload.sticker ?? action.payload.text ?? "";
      resolveSticker(input).forEach((s) => unlocked.add(s));
      return {
        ...state,
        messages: [...state.messages, action.payload],
        unlockedStickers: unlocked,
      };
    }
    case "UNLOCK_STICKER": {
      const unlockedStickers = new Set(state.unlockedStickers);
      unlockedStickers.add(action.payload);
      return { ...state, unlockedStickers };
    }
    case "TOGGLE_STICKERS_MENU":
      return { ...state, showStickersMenu: !state.showStickersMenu };
    case "SET_ALI_TYPING":
      return { ...state, aliIsTyping: action.payload };
    default:
      return state;
  }
}

function App() {
  const [state, dispatch] = useReducer(reducer, undefined, loadState);

  useEffect(() => {
    saveState(state);
  }, [state]);

  return (
    <AppContext.Provider value={{ ...state, dispatch }}>
      <div className="App">
        <ChatWindow />
        <ChatBar />
      </div>
    </AppContext.Provider>
  );
}

export default App;
